import { useCallback, useEffect, useState } from 'react'
import { Container } from 'typescript-ioc/es5'
import { InsightsService } from '../../../services/insights'
import { AccountService } from '../../../services/account'
import { cancelNamedAxiosRequest } from '../../../config/axios-setup'
import { DashboardTemplate, TEMPLATE_SLOTS } from '../template'
import { DateWindow, fetchWindowFor, subscriberHistoryWindowFor } from '../math/windows'
import { DailyRow } from '../math/series'
import {
    acquisitionSeriesContract,
    engagementSeriesContract,
    ORG_DASHBOARD_RESPONSE_SHAPE,
    rowsToDailyRows,
    sendsSeriesContract,
} from './contracts'
import { RegistryCapabilities, offeredMeasures } from './withheld-measures'

/** Whether the daily subscriber levels could be read. A read that succeeded with no rows is available. */
export type SubscriberHistoryAvailability = 'available' | 'unavailable'

/** `readFor` names the range the status describes, so a status never outlives the range it was read for. */
type SeriesState = {
    status: 'loading' | 'ready' | 'error'
    rows: DailyRow[]
    subscriberHistory: SubscriberHistoryAvailability
    readFor: string
}

/*
 * Each request has its own cancellation key, so asking again for another range cancels the previous
 * range's requests instead of letting them finish and be discarded.
 */
const CANCELLATION_KEYS = {
    engagement: 'org-dashboard-engagement',
    sends: 'org-dashboard-sends',
    acquisition: 'org-dashboard-acquisition',
    subscriberHistory: 'org-dashboard-subscriber-history',
}

/**
 * Daily per-domain rows covering the range, its comparison window and its baseline, joined from
 * engagement (by event day), sends (by send day), acquisition and the daily subscriber levels. A
 * challenger request that yields nothing is an error: the insights service reports its own failure and
 * answers undefined. The subscriber history is optional: without it the series is still ready, with
 * no subscriber levels, and `subscriberHistory` says the levels are unavailable so that a failed read is not
 * shown as an organization with no history. Only the subscriber read uses the narrower
 * `subscriberHistoryWindowFor`, because sailfish rejects a window beyond its maximum. The organization's domains are read separately, by `useOrgDashboardDomains`.
 *
 * Each request asks only for the template's measures that the organization's definitions offer, so nothing
 * reads until the definitions have loaded, and a request none of whose measures is offered is not sent.
 */
export const useOrgDashboardSeries = (
    organizationId: number,
    range: DateWindow,
    template: DashboardTemplate,
    capabilities: RegistryCapabilities,
) => {
    const [state, setState] = useState<SeriesState>({
        status: 'loading',
        rows: [],
        subscriberHistory: 'available',
        readFor: '',
    })
    const requestedRange = `${range.since}|${range.through}`
    const [attempt, setAttempt] = useState(0)

    const definitionsLoaded = capabilities.notifications !== undefined && capabilities.prompts !== undefined

    useEffect(() => {
        if (!definitionsLoaded) return
        let cancelled = false
        setState((previous) => ({ ...previous, status: 'loading' }))

        const insights = Container.get(InsightsService)
        const accounts = Container.get(AccountService)
        const window = fetchWindowFor(range)
        const scope = { requestScope: 'organizations' as const, scopeId: organizationId }
        const { seriesMeasures } = TEMPLATE_SLOTS[template]
        const engagementMeasures = offeredMeasures(capabilities.notifications, seriesMeasures.engagement)
        const sendsMeasures = offeredMeasures(capabilities.notifications, seriesMeasures.sends)
        const acquisitionMeasures = offeredMeasures(capabilities.prompts, seriesMeasures.acquisition)

        Promise.all([
            engagementMeasures.length
                ? insights.fetch(engagementSeriesContract(window, engagementMeasures), {
                      ...scope,
                      cancellationKey: CANCELLATION_KEYS.engagement,
                  })
                : [],
            sendsMeasures.length
                ? insights.fetch(sendsSeriesContract(window, sendsMeasures), {
                      ...scope,
                      cancellationKey: CANCELLATION_KEYS.sends,
                  })
                : [],
            acquisitionMeasures.length
                ? insights.fetch(acquisitionSeriesContract(window, acquisitionMeasures), {
                      ...scope,
                      cancellationKey: CANCELLATION_KEYS.acquisition,
                  })
                : [],
            accounts
                .fetchOrganizationSubscriberHistory(
                    organizationId,
                    subscriberHistoryWindowFor(range),
                    CANCELLATION_KEYS.subscriberHistory,
                )
                .catch(() => undefined),
        ])
            .then(([engagement, sends, acquisition, levels]) => {
                if (cancelled) return
                if (!engagement || !sends || !acquisition) {
                    setState((previous) => ({ ...previous, status: 'error', readFor: requestedRange }))
                    return
                }
                setState({
                    status: 'ready',
                    rows: rowsToDailyRows(engagement, sends, acquisition, levels ?? [], ORG_DASHBOARD_RESPONSE_SHAPE),
                    subscriberHistory: levels === undefined ? 'unavailable' : 'available',
                    readFor: requestedRange,
                })
            })
            .catch(() => {
                if (!cancelled) setState((previous) => ({ ...previous, status: 'error', readFor: requestedRange }))
            })

        return () => {
            cancelled = true
            Object.values(CANCELLATION_KEYS).forEach((key) => cancelNamedAxiosRequest(key))
        }
    }, [organizationId, requestedRange, attempt, template, capabilities.notifications, capabilities.prompts])

    // The first render for a new range comes before the effect that starts its read: it is loading, not the old range's result.
    const status = state.readFor === requestedRange ? state.status : 'loading'
    return {
        status,
        rows: state.rows,
        subscriberHistory: state.subscriberHistory,
        retry: useCallback(() => setAttempt((count) => count + 1), []),
    }
}
